import { useEffect, useRef } from 'react'
import { useAuth } from '../auth/store'
import { logger } from '../utils/logger'

/**
 * Хук для управления зумом страницы в зависимости от режима приложения
 * 
 * В Telegram режиме отключает зум для основного интерфейса,
 * но сохраняет возможность зума в модальных окнах просмотра файлов
 */
export const useZoomControl = () => {
  const { isTelegramMode } = useAuth()
  const originalViewportRef = useRef<string>('')
  const metaViewportRef = useRef<HTMLMetaElement | null>(null)

  useEffect(() => {
    // Находим meta viewport тег
    let metaViewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement
    
    if (!metaViewport) {
      // Создаем meta viewport если его нет
      metaViewport = document.createElement('meta')
      metaViewport.name = 'viewport'
      document.head.appendChild(metaViewport)
    }
    
    metaViewportRef.current = metaViewport
    
    // Сохраняем оригинальное значение при первом запуске
    if (!originalViewportRef.current) {
      originalViewportRef.current = metaViewport.content || 'width=device-width, initial-scale=1.0'
    }

    if (isTelegramMode) {
      // В Telegram режиме отключаем зум для основного интерфейса
      metaViewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
      
      // Дополнительно предотвращаем зум через CSS
      const style = document.createElement('style')
      style.id = 'telegram-zoom-control'
      style.textContent = `
        /* Отключаем зум для основного интерфейса в Telegram */
        html, body {
          touch-action: pan-x pan-y !important;
        }
        
        /* Разрешаем touch-действия в модальных окнах просмотра файлов */
        .file-preview-modal,
        .pdf-viewer-modal {
          touch-action: auto !important;
        }
        
        /* Для PDF viewer разрешаем все жесты, так как у него свой zoom */
        .pdf-viewer-modal * {
          touch-action: auto !important;
        }
        
        /* Для изображений в preview разрешаем pinch-zoom */
        .file-preview-modal img {
          touch-action: pinch-zoom !important;
        }
        
        /* Для PDF страниц разрешаем все жесты */
        .react-pdf__Page {
          touch-action: auto !important;
        }
      `
      
      // Удаляем предыдущий стиль если есть
      const existingStyle = document.getElementById('telegram-zoom-control')
      if (existingStyle) {
        existingStyle.remove()
      }
      
      document.head.appendChild(style)
      
      logger.log('🔒 Зум отключен для Telegram режима (кроме просмотра файлов)')
    } else {
      // В браузерном режиме восстанавливаем оригинальные настройки
      metaViewport.content = originalViewportRef.current
      
      // Удаляем стили ограничения зума
      const existingStyle = document.getElementById('telegram-zoom-control')
      if (existingStyle) {
        existingStyle.remove()
      }
      
      logger.log('🔓 Зум включен для браузерного режима')
    }

    // Cleanup функция
    return () => {
      // При размонтировании компонента восстанавливаем оригинальные настройки
      if (metaViewportRef.current && originalViewportRef.current) {
        metaViewportRef.current.content = originalViewportRef.current
      }
      
      const existingStyle = document.getElementById('telegram-zoom-control')
      if (existingStyle) {
        existingStyle.remove()
      }
    }
  }, [isTelegramMode])

  return {
    isTelegramMode,
    isZoomDisabled: isTelegramMode
  }
}
